// Navigator component with Digital HIG
import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import DHBox from "@digital-hig/mui/Box";
// @ts-ignore
import DHTypography from "@digital-hig/mui/Typography";
// @ts-ignore
import DHPopover from "@digital-hig/mui/Popover";
// @ts-ignore
import DHMenuItem from "@digital-hig/mui/MenuItem";
// @ts-ignore
import DHDivider from "@digital-hig/mui/Divider";
import { useCfpAuthUtilities } from '../../providers/CfpAuthContext';
import { cacheManager } from '../../utils/cacheManager';

const Navbar: React.FC = () => {

  const { cfpAuthUtils, isAuthenticated } = useCfpAuthUtilities();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Load user info immediately on mount
  useEffect(() => {
    const info = cfpAuthUtils.getCachedUserInfo?.();
    if (info) {
      setUserInfo(info);
    }
  }, [cfpAuthUtils]); // Run when cfpAuthUtils is available

  // Load cached user info on mount and whenever auth state changes
  useEffect(() => {
    const info = cfpAuthUtils.getCachedUserInfo?.();
    
    if (info) {
      setUserInfo(info);
    } else {
      setUserInfo(null);
    }
  }, [isAuthenticated, cfpAuthUtils]);

  // Listen for user info cached event (fresh login scenario)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      if (detail?.userInfo) {
        setUserInfo(detail.userInfo);
      }
    };
    window.addEventListener('cfp:userInfoCached', handler);
    return () => window.removeEventListener('cfp:userInfoCached', handler);
  }, []);

  // Derive display name & initials (include userName/email fallback)
  const { displayName, email, initials } = useMemo(() => {
    // Always use userInfo.userName and userInfo.email if present
    const name: string | undefined = userInfo?.userName || userInfo?.name || userInfo?.displayName || (userInfo?.given_name && userInfo?.family_name ? `${userInfo.given_name} ${userInfo.family_name}` : undefined);
    const mail: string | undefined = userInfo?.email || undefined;
    const derivedInitials = name ? name.split(/\s+/).filter(Boolean).slice(0,2).map(n=>n[0].toUpperCase()).join('') : '';
    return { displayName: name, email: mail, initials: derivedInitials };
  }, [userInfo]);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      cacheManager.clearUserInfo();
      cacheManager.clearCache();
      cacheManager.clearAllCache();
      await cfpAuthUtils.logout();  
    } catch (e) {
      // Swallow error; already logged inside util
    }
    handleClose();
  };

  return (
    <>
      <DHBox
        sx={{
          display: 'flex',
          padding: '0 72px',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
          background: '#000000',
          boxShadow: '0 -1px 0 0 #666 inset',
          borderBottom: '1px solid #666',
          position: 'relative',
          top: 0,
          left: 0,
          right: 0,
          height: '56px',
          zIndex: 1000,
        }}
      >
        {/* Left side - Autodesk Logo */}
        <DHBox display="flex" alignItems="center" sx={{ height: '100%' }}>
          <DHBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <img 
              src="/images/autodesk.svg" 
              alt="Autodesk"
              style={{
                width: '163px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </DHBox>
        </DHBox>

        {/* Right side - User Button */}
        {initials && (
          <DHBox 
            display="flex" 
            alignItems="center" 
            sx={{ 
              marginLeft: 'auto',
              height: '100%'
            }}
          >
            {/* User Avatar */}
            <DHBox
              onClick={handleAvatarClick}
              aria-haspopup="true"
              aria-controls={menuOpen ? 'user-menu' : undefined}
              aria-expanded={menuOpen ? 'true' : undefined}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#6366f1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': { opacity: 0.9 }
              }}
            >
              <DHTypography
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '12px',
                  fontFamily: 'Artifakt Element, sans-serif'
                }}
              >
                {initials}
              </DHTypography>
            </DHBox>
            <DHPopover
              id="user-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
            >
              {(isAuthenticated || userInfo) && (
                <DHBox sx={{ px: 2, pt: 1, paddingBottom: '12px', maxWidth: 240, textAlign: 'center'}}>
                  {displayName && (
                    <DHTypography variant="body-copy-medium" sx={{ fontWeight: 700, lineHeight: 1.5, mb: 1, fontSize: '16px' }}>
                      {displayName}
                    </DHTypography>
                  )}
                  {email && (
                    <DHTypography
                      variant="body-copy-medium"
                      sx={{
                        display: 'block',
                        mt: 2, // vertical spacing from name
                        mb:2,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={email}
                    >
                      {email}
                    </DHTypography>
                  )}
                </DHBox>
              )}
              {(isAuthenticated || userInfo) && <DHDivider sx={{ marginTop: '4px', marginBottom: '4px' }} />}
              {(isAuthenticated || userInfo) && (
                <DHMenuItem onClick={handleSignOut} data-testid="signout-menu-item" sx={{ justifyContent: 'center' }}>
                  <DHTypography variant="body-copy-medium" sx={{ fontWeight: 500, fontSize: '16px' }}>
                    Sign out
                  </DHTypography>
                </DHMenuItem>
              )}
            </DHPopover>
          </DHBox>
        )}
      </DHBox>

      {/* Dark Sky Navigator */}
      <DHBox
        sx={{
          display: 'flex',
          padding: '0 72px',
          alignItems: 'center',
          alignSelf: 'stretch',
          background: '#000000',
          borderBottom: '1px solid #666',
          position: 'relative',
          top: 0,
          left: 0,
          right: 0,
          height: '40px',
          zIndex: 999,
        }}
      >
        <DHTypography
          variant="headline-small"
          component="div"
          sx={{
            color: '#FFFFFF',
            fontFamily: 'Artifakt Element, sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '125%',
            letterSpacing: '0%',
            fontStyle: 'normal',
          }}
        >
          Dark Sky
        </DHTypography>
      </DHBox>
    </>
  );
};

export default Navbar;

